import _ from 'underscore';
import Backbone from 'backbone';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import { updateGridQuery } from '@bingads-webui-campaign/summary-grid-helper';
import { PageReady } from '@bingads-webui/page-ready';

import { gridsMap } from './constants';
import { isInManagementTab, updateDynamicColumnWidth } from './helper/management-dashboard-helper';
import { updateToolbarAfterSelection, updateToolbarAfterGridInitialized } from './plugins/toolbar-config';
import { getToolbarFacade } from './plugins/toolbar-facade';

const edm = globalContext.requireDefault('component/edm-campaign/index');
const eventDispatcher = globalContext.requireDefault('component/event-dispatcher/index');
const state = globalContext.require('component/state/index');
const logger = globalContext.require('module/service/instrumentation/index');
const { handleSortChange } = globalContext.require('campaignui/feature/bingadsgrid/service/state-manager-helper');

logger.logActivityMarker('AccountsGrid.Module.Loaded');

edm.customerIdType.useAdvertiserCustomerId = false;

export class AccountsGrid extends Backbone.View {
  initialize({
    className = '',
    gridType,
    getGridConfig,
    getPlugins,
    filterService,
    pageName = '',
    isForVNext,
  }) {
    const perfDurationMarker = logger.logActivityDurationMarker(`AccountsGrid.${gridType}.initialize`);

    this.gridType = gridType;
    this.filterService = filterService;
    this.pageName = pageName;
    this.isForVNext = isForVNext;
    this.classNames = ['grid'];
    if (className) {
      this.classNames.push(className);
    }

    this.gridName = gridsMap[gridType].gridName;

    const perfGridViewCreateDurationMarker = logger.logActivityDurationMarker(`AccountsGrid.${gridType}.initialize.GridView.Create`);

    const {
      gridView,
      toolbar,
      columnChooser,
      filter,
      filterState,
      pagination,
      inlineDownload,
      primaryContactBulkEditor,
      dateFilter,
    } = pgridFactory({
      gridConfig: getGridConfig(this),
      plugins: getPlugins(this),
    });

    perfGridViewCreateDurationMarker.markEnd();

    if (!isForVNext) {
      state.on('change:$ReportEmailRule', (model, Rule) => {
        if (Rule) {
          inlineDownload.setReportEmailRule(Rule);
          state.unset('$ReportEmailRule');
        }
      });
    }

    this.gridView = gridView;
    this.toolbar = toolbar;
    this.columnChooser = columnChooser;
    this.filter = filter;
    this.filterState = filterState;
    this.pagination = pagination;
    this.inlineDownload = inlineDownload;
    this.primaryContactBulkEditor = primaryContactBulkEditor;
    this.toolbarFacade = getToolbarFacade({ view: this, isForVNext });
    this.dateFilter = dateFilter;

    if (!isForVNext) {
      updateToolbarAfterGridInitialized({ view: this, gridType });

      this.gridView.on('didUpdate', () => {
        if (isInManagementTab(gridType)) {
          updateDynamicColumnWidth();
        }
        updateToolbarAfterSelection({ view: this, gridType });
      });

      this.gridView.on('didSelect', () => {
        updateToolbarAfterSelection({ view: this, gridType });
      });
    }

    // Only track this for the first load of given grid.
    this.gridView.once('willReload', () => {
      logger.logActivityMarker(`AccountsGrid.${gridType}.gridView.dataFetchStart`);
      eventDispatcher.trigger('accounts-summary.grid-data-fetch');
    });

    let initialGrid = true;
    this.gridView.on('willUpdate', (changes) => {
      if (initialGrid) {
        initialGrid = false;
        return;
      }

      handleSortChange(changes, this.gridView, this.gridName);
    });

    this.gridView.once('didReload', () => {
      this.gridView.once('didRedraw', () => {
        PageReady.pageIsReady(this.pageName);
      }, this);
    });

    if (this.filterService) {
      this.filterService.on('filter-state-changed', () => {
        updateGridQuery(this.gridView, this.filterService.getCombinedFilter(), true);
      });
    }

    // need to handle the page number here, since legacy paginationPlugin will be used if DFM enabled
    if (this.gridView.get('isDataFetchManagerSource') && this.pagination) {
      this.pagination.on('change:page-number', (pageNumber) => {
        this.setPageNumberToState(pageNumber);
      });
    }

    perfDurationMarker.markEnd();
    return this;
  }

  setPageNumberToState(number) {
    state.set('pgridPageNumber', _.defaults({
      [this.gridName]: number,
    }, state.get('pgridPageNumber') || {}));
  }

  render() {
    const perfDurationMarker = logger.logActivityDurationMarker(`AccountsGrid.${this.gridType}.gridView.render`);

    if (this.filterService) {
      this.filterService.refreshSource();
      this.filterService.initializeCombinedFilterPromise.then(() => {
        updateGridQuery(this.gridView, this.filterService.getCombinedFilter(), true);
      });
    }

    this.gridView.render(() => perfDurationMarker.markEnd());
    this.$el.append(this.gridView.el);

    return this;
  }

  remove() {
    if (this.gridView) {
      this.gridView.remove();
    }
    if (this.toolbar.gridToolbarGroup) {
      this.toolbar.gridToolbarGroup.remove();
    }
    if (this.columnChooser) {
      this.columnChooser.remove();
    }
    if (this.filter) {
      this.filter.remove();
    }
    if (this.inlineDownload) {
      this.inlineDownload.remove();
    }
    this.setPageNumberToState(0);
    super.remove();
  }
}
